/**
 * <DestinationGuide />
 * --------------------
 * Renders the full travel-guide layout (sections A → G + sticky anchor nav)
 * for a destination, from a guide-data object.
 *
 *  Usage in DestinationDetail.jsx:
 *      const guide = destinationGuides[slug];
 *      {guide && <DestinationGuide guide={guide} city={d.title} />}
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    Mountain,
    Camera,
    Anchor,
    Coffee,
    Sparkles,
    Sun,
    Image as ImageIcon,
    UtensilsCrossed,
    Bed,
    Car,
    Moon,
    Sunrise,
    Sunset,
    Check,
    ArrowUpRight,
    MapPin,
    ChevronRight,
} from "lucide-react";

const ICONS = {
    nature: Mountain,
    mountain: Mountain,
    culture: Coffee, // mosque/temple use this — Coffee is just a fallback shape
    adventure: Anchor,
    experience: Sparkles,
    wildlife: Anchor,
    beach: Sun,
    viewpoint: Camera,
};
const ICON_BG = {
    nature: "bg-emerald-50 text-emerald-700",
    mountain: "bg-emerald-50 text-emerald-700",
    culture: "bg-amber-50 text-amber-700",
    adventure: "bg-blue-50 text-blue-700",
    experience: "bg-pink-50 text-pink-700",
    wildlife: "bg-orange-50 text-orange-700",
    beach: "bg-cyan-50 text-cyan-700",
    viewpoint: "bg-violet-50 text-violet-700",
};

const RUPEE_SCALE = { "₹": "Budget", "₹₹": "Mid-range", "₹₹₹": "Premium" };
const TAG_PALETTE = {
    Halal: "bg-emerald-100 text-emerald-800",
    Vegetarian: "bg-lime-100 text-lime-800",
    Family: "bg-amber-100 text-amber-800",
    Rooftop: "bg-blue-100 text-blue-800",
    Local: "bg-rose-100 text-rose-800",
    Seafood: "bg-cyan-100 text-cyan-800",
    Waterfront: "bg-sky-100 text-sky-800",
    Budget: "bg-zinc-100 text-zinc-700",
    Breakfast: "bg-yellow-100 text-yellow-800",
    Clean: "bg-emerald-100 text-emerald-800",
    Café: "bg-amber-100 text-amber-800",
    Art: "bg-purple-100 text-purple-800",
    Romantic: "bg-pink-100 text-pink-800",
    Italian: "bg-rose-100 text-rose-800",
    Wellness: "bg-emerald-100 text-emerald-800",
    "Local favourite": "bg-amber-100 text-amber-800",
    "Fine dining": "bg-indigo-100 text-indigo-800",
    "Western breakfast": "bg-yellow-100 text-yellow-800",
};
const tagClass = (t) => TAG_PALETTE[t] || "bg-zinc-100 text-zinc-700";

const NAV_ITEMS = [
    { id: "things-to-do", label: "Things to do", key: "thingsToDo" },
    { id: "photo-spots", label: "Photo spots", key: "photoSpots" },
    { id: "restaurants", label: "Restaurants", key: "restaurants" },
    { id: "gulf-travellers", label: "Gulf travellers", key: "gulfTravellers" },
    { id: "itinerary", label: "Itinerary", key: "itineraries" },
    { id: "where-to-stay", label: "Where to stay", key: "hotels" },
    { id: "getting-there", label: "Getting there", key: "prices" },
];

const SectionTitle = ({ kicker, title }) => (
    <header className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ink-softer)]">{kicker}</div>
        <h2 className="font-hz-display text-3xl md:text-4xl tracking-tight mt-1.5">{title}</h2>
    </header>
);

/* ─── SECTION A: Things to do ─── */
const ThingsToDo = ({ items, city, title }) => (
    <section id="things-to-do" className="scroll-mt-32 py-12" data-testid="guide-things-to-do">
        <SectionTitle kicker="Section A" title={title || `What to do in ${city}`} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {items.map((it, i) => {
                const Icon = ICONS[it.icon] || Sparkles;
                return (
                    <article
                        key={i}
                        className="bg-white border border-[var(--border)] rounded-2xl p-5 hover:shadow-sm transition"
                        data-testid={`thing-${i}`}
                    >
                        <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${ICON_BG[it.icon] || "bg-zinc-100 text-zinc-700"}`}>
                                <Icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="font-hz-display text-lg tracking-tight">{it.name}</h3>
                                <p className="text-sm text-[var(--ink-soft)] mt-1 leading-relaxed">{it.desc}</p>
                                <dl className="mt-3 text-xs text-[var(--ink-softer)] space-y-1">
                                    {it.hours && <div><span className="font-semibold text-[var(--ink-soft)]">Hours:</span> {it.hours}</div>}
                                    {it.price && <div><span className="font-semibold text-[var(--ink-soft)]">Entry:</span> {it.price}</div>}
                                    {it.tip && <div className="bg-amber-50 border border-amber-200 rounded-md px-2 py-1 mt-2"><span className="font-semibold text-amber-900">Tip:</span> <span className="text-amber-800">{it.tip}</span></div>}
                                </dl>
                            </div>
                        </div>
                    </article>
                );
            })}
        </div>
    </section>
);

/* ─── SECTION B: Photo spots ─── */
const PhotoSpots = ({ spots, title }) => {
    const timeIcon = (t) => {
        if (/sunrise/i.test(t)) return Sunrise;
        if (/sunset|golden/i.test(t)) return Sunset;
        return Camera;
    };
    return (
        <section id="photo-spots" className="scroll-mt-32 py-12" data-testid="guide-photo-spots">
            <SectionTitle kicker="Section B" title={title || "Best photo spots"} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {spots.map((s, i) => {
                    const TI = timeIcon(s.bestTime);
                    return (
                        <article
                            key={i}
                            className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden hover:shadow-sm transition"
                            data-testid={`photo-spot-${i}`}
                        >
                            <div className="aspect-[4/3] bg-[var(--bg-surface)] overflow-hidden">
                                <img
                                    src={s.image}
                                    alt={`${s.name} — photo spot`}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                            <div className="p-4">
                                <h3 className="font-hz-display text-base tracking-tight">{s.name}</h3>
                                <div className="flex items-center gap-1.5 text-xs text-[var(--ink-soft)] mt-1.5">
                                    <TI className="w-3.5 h-3.5" />
                                    {s.bestTime}
                                </div>
                                {s.tip && <p className="text-xs text-[var(--ink-softer)] mt-2 leading-relaxed">{s.tip}</p>}
                            </div>
                        </article>
                    );
                })}
            </div>
        </section>
    );
};

/* ─── SECTION C: Restaurants with filters ─── */
const WhereToEat = ({ list, title }) => {
    const [filter, setFilter] = useState("All");
    const FILTERS = [
        { k: "All", match: () => true },
        { k: "Halal ✓", match: (r) => r.halal === true },
        { k: "Vegetarian", match: (r) => r.tags?.includes("Vegetarian") },
        { k: "Family-friendly", match: (r) => r.tags?.includes("Family") },
    ];
    const active = FILTERS.find((f) => f.k === filter) || FILTERS[0];
    const filtered = list.filter(active.match);

    return (
        <section id="restaurants" className="scroll-mt-32 py-12" data-testid="guide-where-to-eat">
            <SectionTitle kicker="Section C" title={title || "Where to eat"} />
            {/* Filter pills */}
            <div className="flex flex-wrap gap-2 mb-5" data-testid="restaurant-filters">
                {FILTERS.map((f) => (
                    <button
                        key={f.k}
                        onClick={() => setFilter(f.k)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                            filter === f.k
                                ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                                : "bg-white text-[var(--ink-soft)] border-[var(--border)] hover:border-[var(--ink)]"
                        }`}
                        data-testid={`restaurant-filter-${f.k.replace(/[^a-z]/gi, "")}`}
                    >
                        {f.k}
                    </button>
                ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {filtered.length === 0 && (
                    <p className="col-span-full text-sm text-[var(--ink-softer)]" data-testid="restaurant-empty">
                        No restaurants match this filter.
                    </p>
                )}
                {filtered.map((r, i) => (
                    <article
                        key={i}
                        className="bg-white border border-[var(--border)] rounded-2xl p-5"
                        data-testid={`restaurant-${i}`}
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <h3 className="font-hz-display text-lg tracking-tight flex items-center gap-2">
                                    <UtensilsCrossed className="w-4 h-4 text-[var(--ink-soft)]" />
                                    {r.name}
                                </h3>
                                <div className="text-xs text-[var(--ink-softer)] mt-0.5">
                                    {r.cuisine} · <span className="font-semibold text-[var(--ink-soft)]">{r.price}</span>{" "}
                                    <span className="text-[var(--ink-softer)]">({RUPEE_SCALE[r.price] || ""})</span>
                                </div>
                            </div>
                            {r.halal && (
                                <span className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800">
                                    <Check className="w-3 h-3 stroke-[3]" /> Halal
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-[var(--ink-soft)] mt-2 leading-relaxed">{r.desc}</p>
                        {r.tags && r.tags.length > 0 && (
                            <ul className="flex flex-wrap gap-1.5 mt-3">
                                {r.tags.map((t, ti) => (
                                    <li key={ti} className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${tagClass(t)}`}>{t}</li>
                                ))}
                            </ul>
                        )}
                    </article>
                ))}
            </div>
        </section>
    );
};

/* ─── SECTION D: Gulf & Muslim travellers (dark card) ─── */
const GulfTravellers = ({ data, title }) => (
    <section id="gulf-travellers" className="scroll-mt-32 py-12" data-testid="guide-gulf-travellers">
        <div className="bg-[#18181b] text-white rounded-3xl p-8 md:p-12 hz-noise relative overflow-hidden">
            <div className="absolute top-6 right-6 text-[10px] uppercase tracking-[0.18em] text-emerald-300/80 font-mono">
                {data.badgeLabel || "Halal • Prayer-friendly"}
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300/80">Section D</div>
            <h2 className="font-hz-display text-3xl md:text-4xl tracking-tight mt-1.5">{title || "For Gulf & Muslim travellers"}</h2>
            {data.arabicTitle && (
                <p className="font-arabic text-base text-white/65 mt-1" dir="rtl">{data.arabicTitle}</p>
            )}

            <dl className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6 text-sm">
                {data.mosque && (
                    <div>
                        <dt className="text-emerald-300/90 text-xs uppercase tracking-wider font-semibold">Nearest mosque</dt>
                        <dd className="text-white/90 mt-1 leading-relaxed">{data.mosque}</dd>
                    </div>
                )}
                {data.halalRestaurants && (
                    <div>
                        <dt className="text-emerald-300/90 text-xs uppercase tracking-wider font-semibold">Halal restaurants</dt>
                        <dd className="text-white/90 mt-1 leading-relaxed">{Array.isArray(data.halalRestaurants) ? data.halalRestaurants.join(" · ") : data.halalRestaurants}</dd>
                    </div>
                )}
                {data.prayerFriendlyHotels && (
                    <div>
                        <dt className="text-emerald-300/90 text-xs uppercase tracking-wider font-semibold">Prayer-friendly hotels</dt>
                        <dd className="text-white/90 mt-1 leading-relaxed">{data.prayerFriendlyHotels}</dd>
                    </div>
                )}
                {data.dryHotels && (
                    <div>
                        <dt className="text-emerald-300/90 text-xs uppercase tracking-wider font-semibold">Dry hotels (no alcohol)</dt>
                        <dd className="text-white/90 mt-1 leading-relaxed">{data.dryHotels}</dd>
                    </div>
                )}
                {data.qibla && (
                    <div className="md:col-span-2">
                        <dt className="text-emerald-300/90 text-xs uppercase tracking-wider font-semibold">Qibla / mosque info</dt>
                        <dd className="text-white/90 mt-1 leading-relaxed">{data.qibla}</dd>
                    </div>
                )}
            </dl>

            {data.note && (
                <p className="mt-8 text-white/75 italic text-sm leading-relaxed border-l-2 border-emerald-400/40 pl-4">
                    {data.note}
                </p>
            )}
            {data.arabicNote && (
                <p className="mt-4 text-white/85 font-arabic text-base leading-loose" dir="rtl">{data.arabicNote}</p>
            )}

            {data.footerNote && (
                <div className="mt-10 pt-6 border-t border-white/15 text-white/80 text-sm">
                    <strong className="text-white">{data.footerNote}</strong>
                </div>
            )}
        </div>
    </section>
);

/* ─── SECTION E: Itinerary tabs ─── */
const ItineraryTabs = ({ data, title, cta }) => {
    const tabs = Object.keys(data);
    const [active, setActive] = useState(tabs[0]);
    const current = data[active];

    const renderSlots = (slots, idx) => (
        <div key={idx} className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {["Morning", "Afternoon", "Evening"].map((slot) =>
                slots[slot] ? (
                    <div key={slot} className="bg-white border border-[var(--border)] rounded-xl p-4">
                        <div className="flex items-center gap-2 text-[var(--ink)] font-semibold text-sm">
                            {slot === "Morning" && <Sunrise className="w-4 h-4 text-amber-500" />}
                            {slot === "Afternoon" && <Sun className="w-4 h-4 text-orange-500" />}
                            {slot === "Evening" && <Moon className="w-4 h-4 text-indigo-500" />}
                            {slot}
                        </div>
                        <p className="text-sm text-[var(--ink-soft)] mt-2 leading-relaxed">{slots[slot]}</p>
                    </div>
                ) : null
            )}
        </div>
    );

    return (
        <section id="itinerary" className="scroll-mt-32 py-12" data-testid="guide-itinerary">
            <SectionTitle kicker="Section E" title={title || "Suggested itinerary"} />
            <div className="flex gap-2 mb-4" role="tablist" data-testid="itinerary-tabs">
                {tabs.map((t) => (
                    <button
                        key={t}
                        role="tab"
                        aria-selected={t === active}
                        onClick={() => setActive(t)}
                        className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
                            t === active
                                ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                                : "bg-white text-[var(--ink-soft)] border-[var(--border)] hover:border-[var(--ink)]"
                        }`}
                        data-testid={`itinerary-tab-${t.replace(" ", "")}`}
                    >
                        {t}
                    </button>
                ))}
            </div>
            {Array.isArray(current)
                ? current.map((day, i) => (
                      <div key={i} className="mt-4 first:mt-2">
                          <div className="font-hz-display text-lg tracking-tight">{day.day}</div>
                          {renderSlots(day, i)}
                      </div>
                  ))
                : renderSlots(current, 0)}

            {(cta?.text || "Book a cab for this itinerary") && (
                <Link to={cta?.url || "/cab-booking"} className="hz-btn-primary mt-8 inline-flex" data-testid="itinerary-cta">
                    {cta?.text || "Book a cab for this itinerary"} <ArrowUpRight className="w-4 h-4" />
                </Link>
            )}
        </section>
    );
};

/* ─── SECTION F: Where to stay (3 cols) ─── */
const WhereToStay = ({ data, title, footerNote }) => {
    const cols = [
        { k: "budget", label: "Budget" },
        { k: "midRange", label: "Mid-range" },
        { k: "luxury", label: "Luxury" },
    ];
    return (
        <section id="where-to-stay" className="scroll-mt-32 py-12" data-testid="guide-where-to-stay">
            <SectionTitle kicker="Section F" title={title || "Where to stay"} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {cols.map(({ k, label }) => (
                    <div
                        key={k}
                        className="bg-white border border-[var(--border)] rounded-2xl p-5"
                        data-testid={`stay-${k}`}
                    >
                        <div className="flex items-center gap-2 text-[var(--ink)] font-semibold">
                            <Bed className="w-4 h-4 text-[var(--ink-soft)]" />
                            <span className="font-hz-display text-base tracking-tight">{label}</span>
                        </div>
                        <ul className="mt-3 space-y-3">
                            {(data[k] || []).map((h, i) => (
                                <li key={i} className="text-sm">
                                    <div className="font-semibold text-[var(--ink)]">{h.name}</div>
                                    <div className="text-[var(--ink-softer)] text-xs mt-0.5">₹{h.price}/night</div>
                                    <div className="text-[var(--ink-soft)] mt-1 leading-relaxed">{h.highlight}</div>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
            <p className="text-xs text-[var(--ink-softer)] mt-5 italic">
                {footerNote || (<>SouthTrip can help arrange accommodation on request — ask your driver or <Link to="/contact" className="hz-link">contact us</Link>.</>)}
            </p>
        </section>
    );
};

/* ─── SECTION G: Getting there with prices ─── */
const GettingThere = ({ rows, city, title, ctas }) => (
    <section id="getting-there" className="scroll-mt-32 py-12" data-testid="guide-getting-there">
        <SectionTitle kicker="Section G" title={title || `Getting to ${city} with SouthTrip`} />
        <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
                <thead className="bg-[var(--bg-surface)] text-[var(--ink)]">
                    <tr>
                        <th className="text-left p-3 font-semibold">
                            <MapPin className="inline w-4 h-4 mr-1 -mt-0.5 text-[var(--ink-soft)]" /> From
                        </th>
                        <th className="text-right p-3 font-semibold">Sedan / day</th>
                        <th className="text-right p-3 font-semibold">SUV / day</th>
                        <th className="text-right p-3 font-semibold hidden md:table-cell"></th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i} className="border-t border-[var(--border)]" data-testid={`price-row-${i}`}>
                            <td className="p-3 text-[var(--ink)] font-medium">
                                {r.from}
                                {r.fixedTransfer && <span className="ml-2 text-[10px] uppercase tracking-wider text-[var(--ink-softer)]">(one-way transfer)</span>}
                            </td>
                            <td className="p-3 text-right font-semibold text-[var(--ink)]">₹{Number(r.sedan).toLocaleString()}</td>
                            <td className="p-3 text-right font-semibold text-[var(--ink)]">₹{Number(r.suv).toLocaleString()}</td>
                            <td className="p-3 text-right hidden md:table-cell">
                                <Link
                                    to="/cab-booking"
                                    className="text-[var(--brand)] hover:underline text-xs font-semibold inline-flex items-center gap-1"
                                >
                                    Book <ChevronRight className="w-3 h-3" />
                                </Link>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
        <div className="flex flex-wrap gap-2 mt-5">
            {(ctas || []).filter((c) => c?.text).slice(0, 3).map((c, i) => (
                <Link key={i} to={c.url || "/cab-booking"} className={`text-sm ${i === 0 ? "hz-btn-primary" : "hz-btn-ghost"}`} data-testid={`getting-there-cta-${i}`}>
                    {c.text} <ArrowUpRight className="w-4 h-4" />
                </Link>
            ))}
        </div>
    </section>
);

/* ─── Quick facts addon (compact 2-col list) ─── */
const QuickFactsExtra = ({ data }) => (
    <div className="mt-4 pt-4 border-t border-[var(--border)]" data-testid="quick-facts-extra">
        <dl className="space-y-2 text-sm">
            {Object.entries(data).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-2">
                    <dt className="text-[var(--ink-softer)] text-xs uppercase tracking-wider capitalize">
                        {k.replace(/([A-Z])/g, " $1")}
                    </dt>
                    <dd className="text-[var(--ink)] text-right font-medium text-xs max-w-[60%]">{v}</dd>
                </div>
            ))}
        </dl>
    </div>
);

/* ─── Sticky anchor nav ─── */
const StickyNav = ({ available }) => (
    <nav
        className="sticky top-16 z-30 bg-[var(--bg)]/90 backdrop-blur border-y border-[var(--border)] -mx-4 sm:-mx-6 px-4 sm:px-6 py-2 overflow-x-auto"
        data-testid="guide-sticky-nav"
        aria-label="Guide sections"
    >
        <ul className="flex gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap">
            {available.map((it) => (
                <li key={it.id}>
                    <a
                        href={`#${it.id}`}
                        className="px-3 py-1.5 rounded-full text-[var(--ink-soft)] hover:bg-[var(--ink)] hover:text-white transition font-medium"
                        data-testid={`guide-nav-${it.id}`}
                    >
                        {it.label}
                    </a>
                </li>
            ))}
        </ul>
    </nav>
);

/* ─── Main wrapper ─── */
const DestinationGuide = ({ guide, city }) => {
    if (!guide) return null;
    const available = NAV_ITEMS.filter((n) => {
        const v = guide[n.key];
        if (!v) return false;
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === "object") return Object.keys(v).length > 0;
        return true;
    });
    const st = guide.sectionTitles || {};

    return (
        <div data-testid="destination-guide">
            <StickyNav available={available} />

            <div className="max-w-7xl mx-auto px-4 sm:px-6">
                {guide.thingsToDo && guide.thingsToDo.length > 0 && (
                    <ThingsToDo items={guide.thingsToDo} city={city} title={st.thingsToDo} />
                )}
                {guide.photoSpots && guide.photoSpots.length > 0 && (
                    <PhotoSpots spots={guide.photoSpots} title={st.photoSpots} />
                )}
                {guide.restaurants && guide.restaurants.length > 0 && (
                    <WhereToEat list={guide.restaurants} title={st.restaurants} />
                )}
                {guide.gulfTravellers && Object.keys(guide.gulfTravellers).length > 0 && (
                    <GulfTravellers data={guide.gulfTravellers} title={st.gulf} />
                )}
                {guide.itineraries && Object.keys(guide.itineraries).length > 0 && (
                    <ItineraryTabs data={guide.itineraries} title={st.itinerary} cta={guide.ctas?.itinerary} />
                )}
                {guide.hotels && Object.keys(guide.hotels).length > 0 && (
                    <WhereToStay data={guide.hotels} title={st.stay} footerNote={guide.hotelsFooterNote} />
                )}
                {guide.prices && guide.prices.length > 0 && (
                    <GettingThere rows={guide.prices} city={city} title={st.gettingThere} ctas={guide.ctas?.gettingThere} />
                )}
            </div>
        </div>
    );
};

export { QuickFactsExtra };
export default DestinationGuide;
